/**
 * src/lib/voice/voiceSseHandler.js — Phase 76-02 Task 3
 *
 * Pure SSE event router for the voice.chat stream. Extracted from
 * useVoiceConversation so the parent hook sits at ≤300 lines (CLAUDE.md).
 *
 * Each event type mutates provided refs / callbacks. No React, no state
 * (the caller owns state). Testable in isolation — pass mock refs + a
 * synthetic event stream.
 */

import { logVoiceEvent } from './voiceLogger';

export function handleVoiceSseEvent(event, ctx) {
  if (!event?.type) return;
  const {
    sessionIdRef,
    setPacketUsed,
    aiTextRef,
    setLastAiText,
    aiFullTextRef,
    setLastAiFullText,
    toolCallsRef,
    setLastToolCalls,
    sentenceBuffer,
    streamDoneRef,
    onToolProposal,
    onConfirmationResolved,
    onToolResult,
  } = ctx;

  const { type, data } = event;

  if (type === 'session' && data?.id) {
    sessionIdRef.current = data.id;
    return;
  }
  if (type === 'packet_used' && data) {
    setPacketUsed(data);
    if (data.truncated?.length) {
      logVoiceEvent('knowledge_packet_truncated', 'ok',
        `truncated: ${data.truncated.join(', ')}`,
        { truncated: data.truncated, tokens: data.tokens });
    }
    return;
  }
  if (type === 'fallback' && data?.provider) {
    logVoiceEvent('voice_fallback', 'ok',
      `Gemini quota/rate — falling back to ${data.provider}`,
      { provider: data.provider, reason: data.reason || 'unknown' });
    return;
  }
  if (type === 'text' && data?.text) {
    const token = data.text;
    aiTextRef.current += token;
    setLastAiText(aiTextRef.current);
    sentenceBuffer?.push(token);
    return;
  }
  if (type === 'full_text' && data?.text) {
    aiFullTextRef.current += data.text;
    setLastAiFullText(aiFullTextRef.current);
    return;
  }
  if (type === 'tool_call' && data?.name) {
    const entry = {
      name: data.name,
      input: data.input || {},
      result: null,
      ok: null,
      at: Date.now(),
    };
    toolCallsRef.current = [...toolCallsRef.current, entry];
    setLastToolCalls(toolCallsRef.current);
    logVoiceEvent('voice_tool_call', 'ok', `${data.name}`, { input: data.input });
    return;
  }
  if (type === 'tool_resolved' && data?.name) {
    const list = toolCallsRef.current;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].name === data.name && list[i].ok === null) {
        list[i] = {
          ...list[i],
          resolutions: data.resolutions || [],
          resolverWarning: data.warning || null,
        };
        break;
      }
    }
    toolCallsRef.current = [...list];
    setLastToolCalls(toolCallsRef.current);
    logVoiceEvent('voice_tool_resolved', 'ok',
      `${data.name}: ${(data.resolutions || []).length} resolution(s)`,
      { resolutions: data.resolutions, warning: data.warning });
    return;
  }
  if (type === 'tool_result' && data?.name) {
    const list = toolCallsRef.current;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].name === data.name && list[i].ok === null) {
        list[i] = { ...list[i], ok: !!data.ok, result: data.result };
        break;
      }
    }
    toolCallsRef.current = [...list];
    setLastToolCalls(toolCallsRef.current);
    logVoiceEvent('voice_tool_result', data.ok ? 'ok' : 'error',
      `${data.name}: ${data.ok ? 'ok' : 'error'}`,
      { ok: data.ok, result: data.result });
    // TICKET-098 — zero-tap learning has no pill, so inline tool results are
    // the only signal a vocab write happened; the hook refreshes its live map.
    onToolResult?.({ name: data.name, ok: !!data.ok, result: data.result });
    return;
  }
  if (type === 'tool_proposal' && data?.proposal_id) {
    logVoiceEvent('voice_tool_proposal', 'ok',
      `${data.name}: ${data.summary || ''}`,
      { proposal_id: data.proposal_id, name: data.name });
    onToolProposal?.({
      proposal_id: data.proposal_id,
      name: data.name,
      resolved_args: data.resolved_args || {},
      summary: data.summary || '',
    });
    return;
  }
  if (type === 'confirmation_resolved' && data?.proposal_id) {
    logVoiceEvent('voice_confirmation_resolved', 'ok',
      `${data.outcome}`,
      { proposal_id: data.proposal_id, outcome: data.outcome });
    onConfirmationResolved?.({
      proposal_id: data.proposal_id,
      outcome: data.outcome,
    });
    return;
  }
  if (type === 'tool_limit') {
    logVoiceEvent('voice_tool_limit', 'error',
      `Hit max tool iterations (${data?.iterations || '?'})`,
      { iterations: data?.iterations });
    return;
  }
  if (type === 'done' || type === 'error') {
    sentenceBuffer?.flush();
    streamDoneRef.current = true;
    if (type === 'error') {
      console.error('[voiceSseHandler] agent error:', data);
    }
  }
}
